import { useEffect, useRef, useState } from 'preact/hooks';
import type { AssemblyProgress, AssemblyStageProgress, RecordingInfo, RecordingPlanEntry, RoomState, TimelineEntry, TranscriptLine } from '@besedka/shared';
import { formatMs } from '@besedka/shared';
import { api, ApiError } from '../lib/api.js';
import { navigate } from '../lib/router.js';

interface Peaks {
  durationMs: number;
  peaks: number[];
}

export function EpisodePage({ episodeId }: { episodeId: number }) {
  const [state, setState] = useState<RoomState | null>(null);
  const [peaks, setPeaks] = useState<Peaks | null>(null);
  const [progress, setProgress] = useState<AssemblyProgress | null>(null);
  const [error, setError] = useState('');
  const [position, setPosition] = useState(0);
  // Перенос темы в следующий выпуск: id → 'pending' | 'ok' | текст ошибки
  const [carry, setCarry] = useState<Record<number, string>>({});
  const audioRef = useRef<HTMLAudioElement>(null);

  const carryOver = async (topicId: number) => {
    setCarry((prev) => ({ ...prev, [topicId]: 'pending' }));
    try {
      await api(`/api/topics/${topicId}/carry-over`, { method: 'POST' });
      setCarry((prev) => ({ ...prev, [topicId]: 'ok' }));
    } catch (e) {
      setCarry((prev) => ({ ...prev, [topicId]: e instanceof Error ? e.message : 'Ошибка' }));
    }
  };

  const load = () =>
    api<RoomState>(`/api/state?episodeId=${episodeId}`)
      .then(setState)
      .catch((e) => {
        if (e instanceof ApiError && e.status === 401) navigate('/login');
        else setError(e instanceof Error ? e.message : 'Ошибка');
      });

  useEffect(() => {
    void load();
  }, [episodeId]);

  // Пока мастер не готов (сборка идёт, артефакты появляются по одному) — поллим
  useEffect(() => {
    if (!state || state.episode.status !== 'finished') return;
    if (state.recordings.some((r) => r.kind === 'master' && r.format === 'mp3')) return;
    const timer = window.setInterval(() => void load(), 10_000);
    return () => window.clearInterval(timer);
  }, [state?.recordings.length, state?.episode.status]);

  useEffect(() => {
    void api<Peaks>(`/api/episodes/${episodeId}/peaks`).then(setPeaks).catch(() => {});
  }, [episodeId, state?.recordings.length]);

  // Прогресс сборки (JSON от worker'а) — раз в 2 с, пока мастер не готов.
  useEffect(() => {
    if (!state || state.episode.status !== 'finished') return;
    if (state.recordings.some((r) => r.kind === 'master' && r.format === 'mp3')) return;
    const poll = () =>
      api<AssemblyProgress>(`/api/episodes/${episodeId}/assembly-progress`)
        .then(setProgress)
        .catch(() => setProgress(null));
    void poll();
    const timer = window.setInterval(poll, 2_000);
    return () => window.clearInterval(timer);
  }, [episodeId, state?.recordings.length, state?.episode.status]);

  if (error) {
    return (
      <main class="shell">
        <p class="notice">{error}</p>
      </main>
    );
  }
  if (!state) {
    return (
      <main class="shell">
        <p class="mutedText">Загрузка…</p>
      </main>
    );
  }

  const episode = state.episode;
  const masterMp3 = state.recordings.find((r) => r.kind === 'master' && r.format === 'mp3');
  const masterRawMp3 = state.recordings.find((r) => r.kind === 'master-raw' && r.format === 'mp3');
  // Мастер регистрируется последним — его наличие означает конец сборки.
  const assemblyDone = Boolean(masterMp3);
  const expectTracks = episode.status === 'finished' && state.recordingPlan.length > 0;
  // Прогресс актуален, только пока сборка не завершена и worker жив (обновлял файл недавно).
  const liveProgress = !assemblyDone && progress ? progress : null;
  const progressStale = Boolean(liveProgress && liveProgress.status === 'running' &&
    Date.now() - new Date(liveProgress.updatedAt).getTime() > 90_000);
  const trackProgress = (participantId: number) => liveProgress?.tracks.find((t) => t.participantId === participantId);

  const seekTo = (ms: number) => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.currentTime = ms / 1000;
    void audio.play();
  };

  return (
    <main class="shell">
      <header class="topbar">
        <div>
          <p class="eyebrow">{state.podcast.title}</p>
          <h1>{episode.title}</h1>
        </div>
        <div class="userbar">
          <button onClick={() => navigate('/')}>← К текущему</button>
        </div>
      </header>

      {episode.deletedAt && (
        <div class="banner err">
          Выпуск в корзине (удалён {new Date(episode.deletedAt).toLocaleString('ru-RU')}): только просмотр.{' '}
          {state.me?.kind === 'user' && (
            <button
              class="small"
              onClick={() => {
                if (!confirm(`Восстановить ${episode.title} из корзины?`)) return;
                void api(`/api/episodes/${episodeId}/restore`, { method: 'POST' })
                  .then(() => void load())
                  .catch((e) => alert(`Не удалось восстановить: ${e instanceof Error ? e.message : e}`));
              }}
            >
              Восстановить
            </button>
          )}
        </div>
      )}
      {episode.status !== 'finished' && !episode.deletedAt && (
        <div class="banner info">Выпуск ещё не завершён.</div>
      )}
      {episode.status === 'finished' && !assemblyDone && (
        <div class="banner info">
          Запись собирается — от простого к сложному: сырые дорожки, raw-микс, потом мастеринг.
          Ссылки появляются по мере готовности, страница обновляется автоматически.{' '}
          {liveProgress && liveProgress.attempt > 1 && <span>Попытка {liveProgress.attempt}, готовые стадии не пересобираются. </span>}
          {progressStale && <span>Worker давно не обновлял прогресс — возможно, сборка прервана. </span>}
          {state.recordings.length === 0 && (
            <button class="small" onClick={() => void api(`/api/episodes/${episodeId}/assemble`, { method: 'POST' }).then(() => void load())}>
              Запустить сборку
            </button>
          )}
        </div>
      )}

      {liveProgress?.status === 'failed' && (
        <div class="banner warn">
          <strong>Сборка упала</strong> (попытка {liveProgress.attempt}): {liveProgress.error}
          <div class="mutedText">Worker повторит попытку автоматически с нарастающей паузой; готовые стадии сохранены.</div>
        </div>
      )}

      {state.gaps.length > 0 && (
        <div class="banner warn">
          <strong>Дыры в записи:</strong>
          <div class="gapList">
            {state.gaps.map((gap, i) => (
              <div key={i}>
                у {gap.participantName}: {formatMs(gap.startMs)}–{formatMs(gap.endMs)}
              </div>
            ))}
          </div>
        </div>
      )}

      {!masterMp3 && expectTracks && (
        <div class="panel">
          <h2>Плеер</h2>
          {liveProgress?.master.status === 'running'
            ? <StageBar stage={liveProgress.master} label="Мастеринг" />
            : <p class="waitStub">Ожидание сборки мастера — мастеринг выполняется в конце, после сырых дорожек…</p>}
          <TimelineList timeline={state.timeline} />
        </div>
      )}
      {masterMp3 && (
        <div class="panel">
          <h2>Плеер</h2>
          <Waveform peaks={peaks} position={position} onSeek={seekTo} />
          <audio
            ref={audioRef}
            controls
            style={{ width: '100%', marginTop: '10px' }}
            src={`/api/recordings/${masterMp3.id}/stream`}
            onTimeUpdate={(e) => setPosition(e.currentTarget.currentTime * 1000)}
          />
          <TimelineList timeline={state.timeline} onSeek={seekTo} />
        </div>
      )}

      {!masterRawMp3 && expectTracks && (
        <div class="panel" style={{ marginTop: '14px' }}>
          <h2>Мастер без обработки (raw)</h2>
          {liveProgress?.masterRaw.status === 'running'
            ? <StageBar stage={liveProgress.masterRaw} label="Сумма дорожек" />
            : <p class="waitStub">Ожидание сборки — соберётся сразу после сырых дорожек участников…</p>}
        </div>
      )}
      {masterRawMp3 && (
        <div class="panel" style={{ marginTop: '14px' }}>
          <h2>Мастер без обработки (raw)</h2>
          <audio
            controls
            style={{ width: '100%' }}
            src={`/api/recordings/${masterRawMp3.id}/stream`}
          />
          <p class="mutedText" style={{ marginTop: '8px' }}>
            Прямая сумма сырых дорожек — без нормализации, компрессии, лимитера и музыки.
            Для сравнения с обработанным мастером; громкость ниже и возможен клиппинг.
          </p>
        </div>
      )}

      {(state.recordings.length > 0 || expectTracks) && (
        <div class="panel downloads" style={{ marginTop: '14px' }}>
          <h2>Скачать дорожки</h2>
          <table>
            <thead>
              <tr>
                <th>Дорожка</th>
                <th>Длительность</th>
                <th>WAV</th>
                <th>MP3</th>
                <th>WAV raw</th>
                <th>MP3 raw</th>
              </tr>
            </thead>
            <tbody>
              {groupRecordings(state.recordings, state.recordingPlan).map((group) => (
                <tr key={group.name}>
                  <td>
                    {group.name}
                    {!assemblyDone && liveProgress && (group.participantId === null
                      ? <><StageBar stage={liveProgress.masterRaw} label="raw" compact /><StageBar stage={liveProgress.master} label="мастер" compact /></>
                      : (() => {
                        const t = trackProgress(group.participantId);
                        return t ? <><StageBar stage={t.raw} label="raw" compact /><StageBar stage={t.processed} label="обработка" compact /></> : null;
                      })())}
                  </td>
                  <td>{group.durationMs ? formatMs(group.durationMs) : '—'}</td>
                  <td><DownloadCell recording={group.wav} done={assemblyDone} /></td>
                  <td><DownloadCell recording={group.mp3} done={assemblyDone} /></td>
                  <td><DownloadCell recording={group.wavRaw} done={assemblyDone} /></td>
                  <td><DownloadCell recording={group.mp3Raw} done={assemblyDone} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div class="panel" style={{ marginTop: '14px' }}>
        <h2>Темы выпуска</h2>
        {state.topics.map((topic) => (
          <div class={`topic status-${topic.status}`} key={topic.id}>
            <div class="body">
              <div class="title">
                {topic.url ? (
                  <a href={topic.url} target="_blank" rel="noreferrer">
                    {topic.title}
                  </a>
                ) : (
                  topic.title
                )}
              </div>
              <div class="meta">
                {topic.author} · {topic.status} · голосов: {topic.voteCount}
              </div>
              {topic.summaryStatus === 'pending' && <div class="articleSummary pending">Готовим recap статьи…</div>}
              {topic.summaryStatus === 'ready' && (
                <div class="articleSummary">
                  <span class="summaryLabel">
                    Recap · {topic.summaryProvider === 'local' ? 'локально' : 'Gemini'}
                  </span>{' '}
                  {topic.articleSummary}
                </div>
              )}
              {topic.summaryStatus === 'failed' && (
                <div class="articleSummary failed">Recap: {topic.summaryError}</div>
              )}
            </div>
            {state.me?.kind === 'user' && episode.status === 'finished' && !episode.deletedAt && (
              <div class="topicActions">
                {carry[topic.id] === 'ok' ? (
                  <span class="mutedText" title="Тема добавлена в следующий выпуск">✓ в след.</span>
                ) : (
                  <button
                    class="small"
                    disabled={carry[topic.id] === 'pending'}
                    title={carry[topic.id] && carry[topic.id] !== 'pending'
                      ? `Не перенеслась: ${carry[topic.id]} — нажмите ещё раз`
                      : 'Перенести тему в следующий выпуск'}
                    onClick={() => void carryOver(topic.id)}
                  >
                    {carry[topic.id] === 'pending' ? '…' : carry[topic.id] ? '⚠ →' : '→ В след.'}
                  </button>
                )}
              </div>
            )}
          </div>
        ))}
        <div class="actions" style={{ marginTop: '10px' }}>
          <a class="buttonLink" href={`/api/episodes/${episodeId}/export/markdown`} target="_blank" rel="noreferrer">
            Markdown
          </a>
          <a class="buttonLink" href={`/api/episodes/${episodeId}/export/youtube`} target="_blank" rel="noreferrer">
            YouTube-таймкоды
          </a>
        </div>
      </div>

      <TranscriptBlock episodeId={episodeId} onSeek={masterMp3 ? seekTo : undefined} />
    </main>
  );
}

// Таймлайн тем: готов ещё во время эфира, поэтому показывается и до сборки
// мастера (в заглушке плеера — без перемотки), и в плеере (с перемоткой).
function TimelineList({ timeline, onSeek }: { timeline: TimelineEntry[]; onSeek?: (ms: number) => void }) {
  if (timeline.length === 0) return null;
  return (
    <div style={{ marginTop: '10px' }}>
      {timeline.map((entry) => (
        <div class="timelineItem" key={entry.id}>
          <span class={onSeek ? 'tc' : 'tc static'} onClick={onSeek ? () => onSeek(entry.startMs) : undefined}>
            {entry.timecode}
          </span>
          <span>{entry.title}</span>
        </div>
      ))}
    </div>
  );
}

// Стенограмма на странице выпуска: последние ~50 строк + ленивая подгрузка назад.
// Клик по реплике — перемотка плеера (если запись собрана).
function TranscriptBlock({ episodeId, onSeek }: { episodeId: number; onSeek?: (ms: number) => void }) {
  const [lines, setLines] = useState<TranscriptLine[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    void api<{ lines: TranscriptLine[] }>(`/api/episodes/${episodeId}/transcript?limit=50`)
      .then((data) => {
        setLines(data.lines);
        setHasMore(data.lines.length >= 50);
        setLoaded(true);
      })
      .catch(() => setLoaded(true));
  }, [episodeId]);

  const loadMore = async () => {
    if (lines.length === 0) return;
    const data = await api<{ lines: TranscriptLine[] }>(
      `/api/episodes/${episodeId}/transcript?beforeId=${lines[0].id}&limit=50`
    ).catch(() => ({ lines: [] as TranscriptLine[] }));
    if (data.lines.length === 0) { setHasMore(false); return; }
    setLines((prev) => [...data.lines, ...prev]);
    setHasMore(data.lines.length >= 50);
  };

  if (loaded && lines.length === 0) return null;

  return (
    <div class="panel" style={{ marginTop: '14px' }}>
      <h2>Стенограмма</h2>
      {hasMore && (
        <button class="small" onClick={() => void loadMore()}>Показать ещё</button>
      )}
      <div class="transcriptLog full">
        {lines.map((line) => (
          <div
            class={`transcriptLine${onSeek ? ' seekable' : ''}`}
            key={line.id}
            onClick={() => onSeek?.(line.startMs)}
          >
            <span class="tc">{formatMs(line.startMs)}</span>
            <span class="who">{line.participantName || 'Спикер'}:</span>
            <span class="text">{line.text}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function sizeText(recording: RecordingInfo): string {
  if (!recording.byteSize) return '';
  const mb = recording.byteSize / (1024 * 1024);
  return mb >= 1024 ? `${(mb / 1024).toFixed(1)} ГБ` : `${Math.round(mb)} МБ`;
}

interface RecordingGroup {
  name: string;
  durationMs: number | null;
  wav?: RecordingInfo;
  mp3?: RecordingInfo;
  wavRaw?: RecordingInfo;
  mp3Raw?: RecordingInfo;
  /** null — строка мастера */
  participantId: number | null;
}

const STEP_LABELS: Record<string, string> = {
  assemble: 'сшивка чанков', mp3: 'MP3', compress: 'компрессор', limit: 'лимитер', normalize: 'нормализация',
  mix: 'сумма', 'bus:sum': 'сумма голосов', 'bus:limit': 'лимитер шины', 'bus:pack': 'шина', premix: 'микс с музыкой',
  measure: 'замер громкости', peak: 'замер пика', wav: 'WAV', encoded: 'контроль MP3', peaks: 'волна'
};

// Полоса прогресса стадии. Пока стадия не начата — ничего (заглушки уже есть),
// готовая/переиспользованная — галочка, упавшая — крестик с ошибкой.
function StageBar({ stage, label, compact }: { stage: AssemblyStageProgress; label: string; compact?: boolean }) {
  if (stage.status === 'pending') return null;
  const percent = Math.round(stage.fraction * 100);
  const step = stage.step ? STEP_LABELS[stage.step] || stage.step : '';
  const title = stage.status === 'failed' ? stage.error || 'ошибка'
    : stage.status === 'reused' ? 'готово с прошлой попытки' : stage.status === 'done' ? 'готово' : `${percent}% ${step}`;
  return (
    <div class={`stageBar ${stage.status}${compact ? ' compact' : ''}`} title={title}>
      <span class="stageLabel">{label}</span>
      <span class="stageTrack"><span class="stageFill" style={{ width: `${percent}%` }} /></span>
      <span class="stageValue">
        {stage.status === 'failed' ? '✕' : stage.status === 'running' ? `${percent}%${compact || !step ? '' : ` · ${step}`}` : '✓'}
      </span>
    </div>
  );
}

// Ссылка на готовый файл; пока сборка идёт — заглушка ожидания, после — «—».
function DownloadCell({ recording, done }: { recording?: RecordingInfo; done: boolean }) {
  if (recording) return <a href={`/api/recordings/${recording.id}/download`}>↓ {sizeText(recording)}</a>;
  if (done) return <span class="mutedText">—</span>;
  return <span class="waitStub" title="Ещё собирается">⏳</span>;
}

// Одна строка на участника (и на мастер): обработанные дорожки — в колонки
// WAV/MP3, сырые (kind *-raw) — в колонки WAV raw/MP3 raw того же ряда.
// План сборки задаёт полный набор строк заранее — у ещё не собранных дорожек
// ячейки остаются пустыми (заглушки рисует DownloadCell).
function groupRecordings(recordings: RecordingInfo[], plan: RecordingPlanEntry[]): RecordingGroup[] {
  const groups = new Map<string, RecordingGroup>();
  for (const entry of plan) {
    groups.set(`p${entry.participantId}`, { name: entry.displayName, durationMs: null, participantId: entry.participantId });
  }
  if (plan.length > 0) groups.set('master', { name: 'Мастер (все голоса)', durationMs: null, participantId: null });
  for (const recording of recordings) {
    const isMaster = recording.kind === 'master' || recording.kind === 'master-raw';
    const isRaw = recording.kind === 'master-raw' || recording.kind === 'track-raw';
    const key = isMaster ? 'master' : `p${recording.participantId}`;
    const name = isMaster ? 'Мастер (все голоса)' : recording.participantName || key;
    const group = groups.get(key) || { name, durationMs: recording.durationMs, participantId: isMaster ? null : recording.participantId };
    if (group.durationMs == null) group.durationMs = recording.durationMs;
    if (recording.format === 'wav') {
      if (isRaw) group.wavRaw = recording;
      else group.wav = recording;
    } else if (isRaw) {
      group.mp3Raw = recording;
    } else {
      group.mp3 = recording;
    }
    groups.set(key, group);
  }
  return [...groups.values()];
}

function Waveform({
  peaks, position, onSeek
}: {
  peaks: Peaks | null;
  position: number;
  onSeek: (ms: number) => void;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  // Canvas не наследует CSS-переменные сам — перерисовываем при смене
  // системной темы, цвета берём из палитры на момент отрисовки.
  const [themeTick, setThemeTick] = useState(0);
  useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => setThemeTick((t) => t + 1);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !peaks) return;
    const dpr = window.devicePixelRatio || 1;
    const width = canvas.clientWidth * dpr;
    const height = canvas.clientHeight * dpr;
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d')!;
    ctx.clearRect(0, 0, width, height);
    const css = getComputedStyle(document.documentElement);
    const playedColor = css.getPropertyValue('--accent').trim() || '#176c63';
    const restColor = css.getPropertyValue('--line').trim() || '#c9c3b6';
    const n = peaks.peaks.length;
    const barWidth = width / n;
    const playedX = peaks.durationMs ? (position / peaks.durationMs) * width : 0;
    for (let i = 0; i < n; i++) {
      const h = Math.max(2, peaks.peaks[i] * height);
      ctx.fillStyle = i * barWidth < playedX ? playedColor : restColor;
      ctx.fillRect(i * barWidth, (height - h) / 2, Math.max(1, barWidth - 1), h);
    }
  }, [peaks, position, themeTick]);

  if (!peaks) return null;

  return (
    <div
      class="waveWrap"
      onClick={(e) => {
        const rect = e.currentTarget.getBoundingClientRect();
        const ratio = (e.clientX - rect.left) / rect.width;
        onSeek(ratio * peaks.durationMs);
      }}
    >
      <canvas ref={canvasRef} />
    </div>
  );
}
